import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  IconButton, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TextField, 
  Select, 
  MenuItem, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Fab,
  Alert,
  useTheme,
  Grid,
  TablePagination,
  CircularProgress
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarMonthIcon 
} from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DownloadIcon from '@mui/icons-material/Download';

dayjs.locale('es');

// Componente para acciones de pagos
const PaymentActions = ({ record, onEdit, onDelete, onConfirmDelete }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <IconButton 
      color="primary" 
      onClick={() => onEdit(record)}
      size="small"
    >
      <EditIcon />
    </IconButton>
    <IconButton 
      color="error" 
      onClick={() => onConfirmDelete(record.IDContabilidad)}
      size="small"
    >
      <DeleteIcon />
    </IconButton>
  </Box>
);

// Componente principal
const ContabilidadSecretaria = () => {
  const theme = useTheme();
  const [pagos, setPagos] = useState([]);
  const [historias, setHistorias] = useState([]);
  const [selectedHistoria, setSelectedHistoria] = useState(null);
  const [editingPago, setEditingPago] = useState(null);
  const [registrarPagoModal, setRegistrarPagoModal] = useState(false);
  const [editarPagoModal, setEditarPagoModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, pagoId: null });
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const API_URL = process.env.REACT_APP_API_URL;
  const [pagePacientes, setPagePacientes] = useState(0);
  const [pagePagos, setPagePagos] = useState(0);
  const rowsPerPage = 5; // Fijo en 5 elementos por página
  const navigate = useNavigate();

  const handleChangePagePacientes = (event, newPage) => {
    setPagePacientes(newPage);
  };

  const handleChangePagePagos = (event, newPage) => {
    setPagePagos(newPage);
  };

  // Filtrar historias del día actual
  const historiasHoy = useMemo(() => {
    const fechaSeleccionada = selectedDate.format('YYYY-MM-DD');
    return historias.filter(historia => 
      dayjs(historia.FechaDeLaHistoria).format('YYYY-MM-DD') === fechaSeleccionada
    );
  }, [historias, selectedDate]);

  // Filtrar pagos del día actual y con monto mayor a 0
  const pagosHoy = useMemo(() => {
    const fechaSeleccionada = selectedDate.format('YYYY-MM-DD');
    return pagos.filter(pago => 
      dayjs(pago.fecha_hora).format('YYYY-MM-DD') === fechaSeleccionada && 
      parseFloat(pago.MontoPagado) > 0
    );
  }, [pagos, selectedDate]);

  // Calcular totales (no necesita cambios ya que pagosHoy ya está filtrado)
  const totales = useMemo(() => {
    return pagosHoy.reduce((acc, pago) => {
        const monto = parseFloat(pago.MontoPagado) || 0;
        acc[pago.forma_pago] = (acc[pago.forma_pago] || 0) + monto;
        acc.total += monto;
        return acc;
    }, { total: 0 });
  }, [pagosHoy]);

  // Obtener datos
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const formattedDate = selectedDate.format('YYYY-MM-DD');
      const [pacientesRes, pagosRes] = await Promise.all([
        axios.get(`${API_URL}/api/pacientes-dia`, {
          params: { fecha: formattedDate }
        }),
        axios.get(`${API_URL}/api/contabilidad/secretaria`, {
          params: { fecha: formattedDate }
        })
      ]);
      
      setHistorias(pacientesRes.data.map(h => ({
        ...h,
        key: h.IDHistoria,
        monto_pendiente: Number(h.monto_pendiente || 0)
      })));
      
      setPagos(pagosRes.data.map(p => ({
        ...p,
        key: p.IDContabilidad,
        MontoPagado: Number(p.MontoPagado),
        MontoPendiente: Number(p.MontoPendiente)
      })));
      setError('');
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Error al cargar datos. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [API_URL, selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handler para registrar pago
  const handleSubmitPago = useCallback(async (formData) => {
    try {
      await axios.post(`${API_URL}/api/contabilidad`, {
        IDHistoria: selectedHistoria.IDHistoria,
        IDPac: selectedHistoria.idpac,
        monto_pagado: formData.monto_pagado,
        forma_pago: formData.forma_pago
      });
      
      await fetchData();
      setRegistrarPagoModal(false);
    } catch (err) {
      console.error('Error al registrar pago:', err);
      setError('Error al registrar el pago. Por favor, verifique los datos.');
    }
  }, [selectedHistoria, API_URL, fetchData]);

  // Modificar el handler para editar pago
  const handleEditPago = useCallback(async (formData) => {
    if (!editingPago) return;
    
    try {
      await axios.put(`${API_URL}/api/contabilidad/${editingPago.IDContabilidad}`, {
        monto_pagado: formData.monto_pagado,
        forma_pago: formData.forma_pago
      });

      // Refrescar todos los datos
      await fetchData();
      setEditarPagoModal(false);
      setEditingPago(null);
    } catch (err) {
      console.error('Error al actualizar pago:', err);
      setError('Error al actualizar el pago. Por favor, intente nuevamente.');
    }
  }, [editingPago, API_URL, fetchData]);

  // Modificar el handler para eliminar pago
  const handleDeletePago = useCallback(async (pagoId) => {
    try {
      await axios.delete(`${API_URL}/api/contabilidad/${pagoId}`);
      
      // Refrescar todos los datos en lugar de solo actualizar el estado local
      await fetchData();
      
      // Cerrar el diálogo de confirmación
      setDeleteConfirmation({ open: false, pagoId: null });
    } catch (err) {
      console.error('Error al eliminar pago:', err);
      setError('Error al eliminar el pago. Por favor, intente nuevamente.');
    }
  }, [API_URL, fetchData]);

  // Columnas para la tabla de pacientes del día
  const pacientesDiaColumns = [
    { id: 'NombrePaciente', label: 'Paciente' },
    { 
      id: 'FechaDeLaHistoria', 
      label: 'Fecha',
      format: d => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : 'N/A'
    },
    { 
      id: 'costo_consulta', 
      label: 'Monto Cobrado',
      format: v => `₡${(v || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}`,
      align: 'right'
    },
    { 
      id: 'monto_pendiente', 
      label: 'Monto Pendiente',
      format: v => {
        const pendiente = v || 0;
        return (
          <span style={{ 
            color: pendiente > 0 ? theme.palette.error.main : theme.palette.success.main, 
            fontWeight: 500 
          }}>
            ₡{pendiente.toLocaleString('es-CR', { minimumFractionDigits: 2 })}
          </span>
        );
      },
      align: 'right'
    },
    {
      id: 'acciones',
      label: 'Acciones',
      format: (_, record) => (
        <Button
          variant="contained"
          size="small"
          disabled={record.monto_pendiente <= 0}
          onClick={() => {
            setSelectedHistoria(record);
            setRegistrarPagoModal(true);
          }}
        >
          Registrar Pago
        </Button>
      )
    }
  ];

  // Columnas para la tabla de pagos
  const pagosColumns = [
    { id: 'NombrePaciente', label: 'Paciente', align: 'left' },
    { 
      id: 'fecha_hora', 
      label: 'Fecha',
      format: d => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : 'N/A',
      align: 'left'
    },
    { 
      id: 'MontoCobrado', 
      label: 'Monto Cobrado',
      format: v => `₡${Number(v).toLocaleString('es-CR', { minimumFractionDigits: 2 })}`,
      align: 'right'
    },
    { 
      id: 'MontoPagado', 
      label: 'Monto Pagado',
      format: v => `₡${Number(v).toLocaleString('es-CR', { minimumFractionDigits: 2 })}`,
      align: 'right'
    },
    { 
      id: 'forma_pago', 
      label: 'Forma Pago',
      format: v => v ? v.charAt(0).toUpperCase() + v.slice(1) : 'No especificado',
      align: 'left'
    },
    { 
      id: 'MontoPendiente', 
      label: 'Monto Pendiente',
      format: v => `₡${Number(v).toLocaleString('es-CR', { minimumFractionDigits: 2 })}`,
      align: 'right'
    },
    {
      id: 'acciones',
      label: 'Acciones',
      format: (_, record) => (
        <PaymentActions 
          record={record}
          onEdit={(rec) => {
            setEditingPago(rec);
            setEditarPagoModal(true);
          }}
          onDelete={handleDeletePago}
          onConfirmDelete={(pagoId) => setDeleteConfirmation({ open: true, pagoId })}
        />
      ),
      align: 'center'
    }
  ];

  // NUEVO: Exportar a Excel (solo pagos del día)
  const exportToExcel = () => {
    const headers = [
      'Paciente',
      'Fecha',
      'Monto Cobrado',
      'Monto Pagado',
      'Forma Pago',
      'Monto Pendiente'
    ];
    const rows = pagosHoy.map(row => [
      row.NombrePaciente,
      dayjs(row.fecha_hora).format('DD/MM/YYYY HH:mm'),
      Number(row.MontoCobrado),
      Number(row.MontoPagado),
      row.forma_pago ? row.forma_pago.charAt(0).toUpperCase() + row.forma_pago.slice(1) : '',
      Number(row.MontoPendiente)
    ]);
    // Fila de totales
    rows.push([
      'Totales',
      '',
      '',
      pagosHoy.reduce((sum, r) => sum + Number(r.MontoPagado), 0),
      '',
      pagosHoy.reduce((sum, r) => sum + Number(r.MontoPendiente), 0)
    ]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = [
      { wch: 20 }, // Paciente
      { wch: 18 }, // Fecha
      { wch: 16 }, // Monto Cobrado
      { wch: 16 }, // Monto Pagado
      { wch: 16 }, // Forma Pago
      { wch: 16 }, // Monto Pendiente
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pagos del Día');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), `Pagos_Secretaria_${selectedDate.format('DD_MM_YYYY')}.xlsx`);
  };

  return (
    <Box sx={{ 
        display: 'flex',
        padding: '64px 16px 16px 16px',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        minHeight: 'calc(100vh - 64px)',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        position: 'relative',
        boxSizing: 'border-box',
        margin: 0
    }}>
      <Paper
        elevation={3}
        sx={{
            padding: { xs: 2, sm: 3 },
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            borderRadius: 2,
            margin: '0',
            boxSizing: 'border-box',
            background: `linear-gradient(135deg, ${theme.palette.background.default} 0%,rgb(238, 245, 178) 100%)`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            height: 'auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            marginTop: '650px'
        }}
      >
        <Box sx={{ 
            display: 'flex',
            padding: '16px',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            position: 'relative',
            boxSizing: 'border-box',
            margin: 0
        }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 'bold', 
            color: theme.palette.primary.main,
            mb: 2
          }}>
            Contabilidad Secretaría
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 2, 
            flexWrap: 'wrap' 
          }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Seleccionar Fecha"
                value={selectedDate}
                onChange={(newValue) => {
                  setSelectedDate(newValue);
                }}
                format="DD/MM/YYYY"
                sx={{ flex: 1, minWidth: 180 }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined"
                  }
                }}
                maxDate={dayjs()}
              />
            </LocalizationProvider>
            <Button 
              variant="contained" 
              startIcon={<RefreshIcon />} 
              onClick={fetchData}
              sx={{ 
                minWidth: 120,
                height: '56px',
                ml: 1
              }}
            >
              Actualizar
            </Button>
            <Button 
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<CalendarMonthIcon />}
              onClick={() => navigate('/contabilidad-mensual')}
              sx={{ 
                minWidth: 140,
                height: '56px',
                ml: 1,
                fontWeight: 'bold'
              }}
            >
              Contabilidad Mensual
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              sx={{ height: 56, fontWeight: 'bold', minWidth: 180, ml: 1 }}
              disabled={pagosHoy.length === 0}
            >
              Exportar a Excel
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Fab 
          color="primary" 
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
          onClick={() => setRegistrarPagoModal(true)}
        >
          <AddIcon />
        </Fab>

        {/* Sección: Pacientes del día */}
        <Typography variant="h6" sx={{ 
          fontWeight: 'bold', 
          color: theme.palette.primary.main,
          mb: 2
        }}>
          Pacientes del Día ({selectedDate.format('DD/MM/YYYY')})
        </Typography>
        
        <TableContainer 
          component={Paper} 
          sx={{ 
            maxHeight: '300px', // Reducir altura máxima
            width: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
            boxShadow: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            '& .MuiTable-root': {
                minWidth: 650 // Asegura un ancho mínimo
            }
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {pacientesDiaColumns.map((column) => (
                  <TableCell 
                    key={column.id}
                    align={column.align || 'left'}
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: theme.palette.primary.light,
                      color: 'white'
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={pacientesDiaColumns.length} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography>Cargando datos...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : historiasHoy.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={pacientesDiaColumns.length} align="center" sx={{ py: 4 }}>
                    <Typography>No se encontraron pacientes para hoy</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                historiasHoy
                  .slice(pagePacientes * rowsPerPage, pagePacientes * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.IDHistoria}>
                      {pacientesDiaColumns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell 
                            key={column.id} 
                            align={column.align || 'left'}
                          >
                            {column.format ? column.format(value, row) : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={historiasHoy.length}
          page={pagePacientes}
          onPageChange={handleChangePagePacientes}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          labelRowsPerPage="Filas por página:"
        />

        {/* Sección: Registro de Pagos */}
        <Typography variant="h6" sx={{ 
          fontWeight: 'bold', 
          color: theme.palette.primary.main,
          mb: 2
        }}>
          Registro de Pagos del Día ({selectedDate.format('DD/MM/YYYY')})
        </Typography>
        
        <TableContainer 
          component={Paper} 
          sx={{ 
            maxHeight: 400,
            boxShadow: 'none',
            border: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {pagosColumns.map((column) => (
                  <TableCell 
                    key={column.id}
                    align={column.align || 'left'}
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: theme.palette.primary.light,
                      color: 'white'
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={pagosColumns.length} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography>Cargando datos...</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : pagos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={pagosColumns.length} align="center" sx={{ py: 4 }}>
                    <Typography>No se encontraron registros de pagos</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagosHoy
                  .slice(pagePagos * rowsPerPage, pagePagos * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.IDContabilidad}>
                      {pagosColumns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell 
                            key={column.id} 
                            align={column.align || 'left'}
                          >
                            {column.format ? column.format(value, row) : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagosHoy.length}
          page={pagePagos}
          onPageChange={handleChangePagePagos}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          labelRowsPerPage="Filas por página:"
        />

        {/* Totalizadores */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {/* Nuevo elemento para mostrar cantidad de pagos */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, backgroundColor: '#fce4ec', mb: 2 }}>
              <Typography variant="subtitle1" align="center">
                Total Pagos Realizados Hoy
              </Typography>
              <Typography variant="h5" align="center" color="primary.main" fontWeight="bold">
                {pagosHoy.length} {pagosHoy.length === 1 ? 'Pago' : 'Pagos'}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, backgroundColor: '#e8f5e9' }}>
              <Typography variant="subtitle1" align="center">
                Efectivo
              </Typography>
              <Typography variant="h6" align="center" color="success.main">
                ₡{(totales.efectivo || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, backgroundColor: '#e3f2fd' }}>
              <Typography variant="subtitle1" align="center">
                Tarjeta
              </Typography>
              <Typography variant="h6" align="center" color="info.main">
                ₡{(totales.tarjeta || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, backgroundColor: '#f3e5f5' }}>
              <Typography variant="subtitle1" align="center">
                Sinpe Móvil
              </Typography>
              <Typography variant="h6" align="center" color="secondary.main">
                ₡{(totales['sinpe movil'] || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, backgroundColor: '#fff3e0' }}>
              <Typography variant="subtitle1" align="center">
                Transferencia
              </Typography>
              <Typography variant="h6" align="center" color="warning.main">
                ₡{(totales['transferencia bancaria'] || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, backgroundColor: '#e0f7fa', mt: 2 }}>
              <Typography variant="h6" align="center">
                Total General
              </Typography>
              <Typography variant="h5" align="center" color="primary.main" fontWeight="bold">
                ₡{totales.total.toLocaleString('es-CR', { minimumFractionDigits: 2 })}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Modal para registrar pago */}
        <Dialog 
          open={registrarPagoModal} 
          onClose={() => setRegistrarPagoModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            fontWeight: 'bold', 
            color: theme.palette.primary.main,
            backgroundColor: '#f5f5f5'
          }}>
            Registrar Pago
          </DialogTitle>
          <DialogContent>
            <PaymentForm 
              paciente={selectedHistoria}
              onSubmit={handleSubmitPago}
              onCancel={() => setRegistrarPagoModal(false)}
              isEditing={false}
            />
          </DialogContent>
        </Dialog>

        {/* Modal para editar pago */}
        <Dialog 
          open={editarPagoModal} 
          onClose={() => setEditarPagoModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            fontWeight: 'bold', 
            color: theme.palette.primary.main,
            backgroundColor: '#f5f5f5'
          }}>
            Editar Pago
          </DialogTitle>
          <DialogContent>
            <PaymentForm 
              paciente={editingPago}
              onSubmit={handleEditPago}
              onCancel={() => setEditarPagoModal(false)}
              isEditing={true}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={deleteConfirmation.open}
          onClose={() => setDeleteConfirmation({ open: false, pagoId: null })}
        >
          <DialogTitle sx={{ 
            fontWeight: 'bold',
            color: theme.palette.error.main
          }}>
            Confirmar Eliminación
          </DialogTitle>
          <DialogContent>
            <Typography>
              ¿Está seguro que desea eliminar este registro de pago? Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2 }}>
            <Button 
              onClick={() => setDeleteConfirmation({ open: false, pagoId: null })}
              variant="outlined"
            >
              Cancelar
            </Button>
            <Button 
              onClick={() => {
                handleDeletePago(deleteConfirmation.pagoId);
                setDeleteConfirmation({ open: false, pagoId: null });
              }}
              variant="contained"
              color="error"
            >
              Eliminar
            </Button>
          </Box>
        </Dialog>
      </Paper>
    </Box>
  );
};

// Componente para el formulario de pago (usado en ambos modales)
const PaymentForm = ({ paciente, onSubmit, onCancel, isEditing }) => {
  const [montoPagado, setMontoPagado] = useState(isEditing && paciente ? paciente.MontoPagado : 0);
  const [formaPago, setFormaPago] = useState(isEditing && paciente ? paciente.forma_pago : 'efectivo');
  const [error, setError] = useState('');

  // Add null checks
  const saldoPendiente = isEditing && paciente ? paciente.MontoPendiente : (paciente?.monto_pendiente || 0);
  const montoTotal = isEditing && paciente ? paciente.MontoCobrado : (paciente?.costo_consulta || 0);

  // Add useEffect to update form when paciente changes
  useEffect(() => {
    if (paciente) {
      setMontoPagado(isEditing ? paciente.MontoPagado : 0);
      setFormaPago(isEditing ? paciente.forma_pago : 'efectivo');
    }
  }, [paciente, isEditing]);

  // Add loading state while paciente is null
  if (!paciente) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSubmit = () => {
    // Validaciones
    if (saldoPendiente <= 0 && !isEditing) {
      setError('No hay saldo pendiente para este paciente');
      return;
    }

    if (montoPagado <= 0) {
      setError('El monto pagado debe ser mayor a 0');
      return;
    }

    if (montoPagado > saldoPendiente && !isEditing) {
      setError(`El monto pagado (₡${montoPagado}) no puede ser mayor al saldo pendiente (₡${saldoPendiente})`);
      return;
    }

    if (montoPagado > montoTotal && isEditing) {
      setError('El monto pagado no puede ser mayor al monto cobrado');
      return;
    }

    onSubmit({
      monto_pagado: montoPagado,
      forma_pago: formaPago
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="bold">
            Paciente: 
          </Typography>
          <Typography variant="body1">
            {paciente?.NombrePaciente || 'No seleccionado'}
          </Typography>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" fontWeight="bold">
            Monto Total: 
          </Typography>
          <Typography variant="body1">
            ₡{montoTotal.toLocaleString('es-CR', { minimumFractionDigits: 2 })}
          </Typography>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" fontWeight="bold">
            Saldo Pendiente: 
          </Typography>
          <Typography variant="body1" color={saldoPendiente > 0 ? 'error' : 'success'}>
            ₡{saldoPendiente.toLocaleString('es-CR', { minimumFractionDigits: 2 })}
          </Typography>
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Monto Pagado"
            type="number"
            value={montoPagado}
            onChange={(e) => {
              const value = Math.min(Number(e.target.value), isEditing ? montoTotal : saldoPendiente);
              setMontoPagado(value);
              setError('');
            }}
            inputProps={{ 
              min: 0, 
              max: isEditing ? montoTotal : saldoPendiente 
            }}
            disabled={saldoPendiente <= 0 && !isEditing}
            error={!!error}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Select
            fullWidth
            label="Forma de pago"
            value={formaPago}
            onChange={(e) => setFormaPago(e.target.value)}
          >
            <MenuItem value="efectivo">EFECTIVO</MenuItem>
            <MenuItem value="tarjeta">TARJETA</MenuItem>
            <MenuItem value="sinpe movil">SINPE MÓVIL</MenuItem>
            <MenuItem value="transferencia bancaria">TRANSFERENCIA BANCARIA</MenuItem>
          </Select>
        </Grid>
        
        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}
        
        {montoPagado > 0 && !isEditing && (
          <Grid item xs={12}>
            <Alert severity="info">
              Monto pendiente después del pago: 
              ₡{(saldoPendiente - montoPagado).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
            </Alert>
          </Grid>
        )}
        
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
          <Button variant="outlined" onClick={onCancel}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={
              (saldoPendiente <= 0 && !isEditing) || 
              montoPagado <= 0 || 
              (montoPagado > saldoPendiente && !isEditing) ||
              (montoPagado > montoTotal && isEditing)
            }
          >
            {isEditing ? 'Actualizar Pago' : 'Registrar Pago'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContabilidadSecretaria;